// Decide o que o relatório mostra para cada mensagem.
//
// O documento da mensagem carrega DOIS campos independentes que podem se
// contradizer. O `ack` é a confirmação que vem do WhatsApp e nunca é rebaixado;
// o `status` é reescrito toda vez que a mensagem passa pelo motor de envio.
// Quando a conexão cai no meio de um disparo, grava-se `status: 'erro'` e o
// motivo fica grudado no documento — mas se o ack já provou que o aparelho do
// destinatário recebeu, quem manda é o ack. Sem essa regra o cliente vê alerta
// vermelho numa mensagem que chegou, reenvia à toa e abre chamado.
//
// Este módulo é a ÚNICA fonte dessa decisão: a tabela e o card do relatório
// consultam daqui para nunca divergirem entre si.

// Confirmações do WhatsApp, na ordem em que chegam.
export const ACK_RECEBIDA_NO_SERVIDOR = 1;
export const ACK_RECEBIDA_NO_APARELHO = 2;
export const ACK_LIDA = 3;

// Chaves visuais — cada uma vira um ícone na tabela e um alerta no card.
export const STATUS_ENTREGUE_APESAR_DO_ERRO = "entregue_apesar_do_erro";
export const STATUS_SUCESSO = "sucesso";
export const STATUS_FILA = "fila";
export const STATUS_ADIADA_ANTIBAN = "adiada_antiban";
export const STATUS_SUPRIMIDA = "suprimida";
export const STATUS_BLOQUEADA_SMARTFILTER = "bloqueada_smartfilter";
export const STATUS_CONTATO_BLOQUEADO = "contato_bloqueado";
export const STATUS_ERRO = "erro";
export const STATUS_DESCONHECIDO = "desconhecido";

function ackDe(mensagem) {
  const ack = Number(mensagem && mensagem.ack);
  return Number.isFinite(ack) ? ack : 0;
}

/** O aparelho do destinatário confirmou o recebimento (ack 2) ou a leitura (ack 3). */
export function foiEntregueAoDestinatario(mensagem) {
  return ackDe(mensagem) >= ACK_RECEBIDA_NO_APARELHO;
}

/**
 * Mensagem marcada como erro que, na prática, chegou ao destinatário.
 *
 * É o caso do documento que ficou com um motivo de erro antigo grudado depois de
 * uma tentativa que falhou: a entrega aconteceu, o ack provou, mas o status
 * nunca foi corrigido. Aparece como entregue, com o motivo antigo em aviso
 * discreto — nunca como falha.
 */
export function entregueApesarDoErro(mensagem) {
  return (
    !!mensagem && mensagem.status === "erro" && foiEntregueAoDestinatario(mensagem)
  );
}

/** Descreve o estágio de entrega a partir do ack, sem olhar o status. */
export function descreverAck(ack) {
  const valor = Number(ack);

  if (valor >= ACK_LIDA) return "Mensagem lida";
  if (valor >= ACK_RECEBIDA_NO_APARELHO) return "Recebida no aparelho do destinatário";
  if (valor >= ACK_RECEBIDA_NO_SERVIDOR) return "Recebida no servidor do WhatsApp";

  return "Aguardando confirmação do WhatsApp";
}

function formatarQuando(data) {
  if (!data) return "";

  const quando = new Date(data);
  if (Number.isNaN(quando.getTime())) return "";

  return quando.toLocaleString("pt-br", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Traduz a mensagem crua num descritor único de exibição.
 *
 * `severidade` segue a escala do Alert do MUI, então serve tanto para o card
 * quanto para a cor do ícone na tabela. `rotulo` é o texto curto do tooltip e
 * `descricao` é a frase que explica a situação ao cliente dentro do card.
 */
export function resolveStatusVisual(mensagem) {
  const status = (mensagem && mensagem.status) || "";
  const ack = ackDe(mensagem);

  if (entregueApesarDoErro(mensagem)) {
    const lida = ack >= ACK_LIDA;
    return {
      chave: STATUS_ENTREGUE_APESAR_DO_ERRO,
      severidade: "warning",
      rotulo: lida ? "Entregue e lida" : "Entregue ao destinatário",
      descricao:
        `Entregue ao destinatário${lida ? " e lida" : ""}. Uma tentativa anterior ` +
        "falhou e o motivo ficou registrado — não é preciso reenviar.",
      ack,
    };
  }

  if (status === "success") {
    return {
      chave: STATUS_SUCESSO,
      severidade: "success",
      rotulo: descreverAck(ack),
      descricao: "",
      ack,
    };
  }

  if (status === "queue") {
    return {
      chave: STATUS_FILA,
      severidade: "info",
      rotulo: "Na fila de envio",
      descricao:
        "Aguardando a vez na fila. Sai automaticamente assim que a conexão " +
        "estiver disponível e o intervalo entre envios permitir.",
      ack,
    };
  }

  if (status === "antiban_delayed") {
    const quando = formatarQuando(mensagem && mensagem.sendAt);
    return {
      chave: STATUS_ADIADA_ANTIBAN,
      severidade: "warning",
      rotulo: "Adiada pelo anti-ban",
      descricao:
        "Adiada pelo anti-ban (teto diário ou aquecimento do número). O reenvio é " +
        `automático${quando ? `, a partir de ${quando}` : ""} — não é preciso fazer nada.`,
      ack,
    };
  }

  if (status === "suppressed") {
    return {
      chave: STATUS_SUPRIMIDA,
      severidade: "info",
      rotulo: "Destinatário optou por não receber",
      descricao:
        "Não enviada: o destinatário pediu para parar de receber mensagens " +
        "(opt-out) e está na lista de supressão.",
      ack,
    };
  }

  if (status === "smartfilter_blocked") {
    return {
      chave: STATUS_BLOQUEADA_SMARTFILTER,
      severidade: "info",
      rotulo: "Retida pelo SmartFilter",
      descricao:
        "Retida pelo SmartFilter e não aprovada para envio — nenhuma cobrança foi " +
        "gerada por ela.",
      ack,
    };
  }

  // Legado: o motor não grava mais esse status, mas o histórico antigo tem.
  if (status === "blocked_contact") {
    return {
      chave: STATUS_CONTATO_BLOQUEADO,
      severidade: "error",
      rotulo: "Contato bloqueado",
      descricao: "Não enviada: este número está bloqueado.",
      ack,
    };
  }

  if (status === "erro") {
    return {
      chave: STATUS_ERRO,
      severidade: "error",
      rotulo: "Falha no envio",
      descricao: "",
      ack,
    };
  }

  return {
    chave: STATUS_DESCONHECIDO,
    severidade: "info",
    rotulo: status ? `Status não identificado (${status})` : "Status não identificado",
    descricao: "",
    ack,
  };
}

/**
 * Rótulo do estágio de entrega para a exportação em CSV.
 *
 * Fica aqui de propósito: o CSV precisa contar a MESMA história que a tela.
 */
export function ackParaCsv(mensagem) {
  const ack = ackDe(mensagem);

  if (ack >= ACK_LIDA) return "read";
  if (ack >= ACK_RECEBIDA_NO_APARELHO) return "delivered";
  if (ack >= ACK_RECEBIDA_NO_SERVIDOR) return "sent";

  return "not_sent";
}

/** Status para o CSV, já com a correção de entregue-apesar-do-erro aplicada. */
export function statusParaCsv(mensagem) {
  if (entregueApesarDoErro(mensagem)) return "success";

  return (mensagem && mensagem.status) || "";
}
